// Compact event pill rendered inside the collapsed venue card and the
// map preview sheet. Shows the soonest active/upcoming event for the
// venue so guests see "what's on" without opening the card.

import React from 'react';
import { PartyPopper } from 'lucide-react';
import { relativeEventLabel } from './EventsCarousel';
import { EVENT_CATEGORY_LABELS } from './eventCategoryLabels';

const font = "'Inria Serif', serif";

export function VenueEventChip({ event, compact = false }) {
  if (!event) return null;
  const title = event.display_title || event.custom_category || EVENT_CATEGORY_LABELS[event.category] || 'Event';
  const label = relativeEventLabel(event);
  const isLive = label.startsWith('Live Now');
  const isStarting = label.startsWith('Starting Now');
  const accent = isLive
    ? { bg: 'rgba(255, 80, 80, 0.12)', dotBg: '#FF5050', text: '#C42626' }
    : isStarting
      ? { bg: 'rgba(255, 142, 56, 0.14)', dotBg: '#FF8E38', text: '#B0531A' }
      : { bg: 'var(--accent-surface)', dotBg: 'var(--accent-purple)', text: 'var(--accent-text-soft)' };

  return (
    <div
      data-testid={`venue-event-chip-${event.id}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: compact ? '6px 10px' : '8px 12px',
        borderRadius: 999,
        background: accent.bg,
        color: accent.text,
        fontFamily: font, fontSize: compact ? 12 : 13, fontWeight: 700,
        maxWidth: '100%', minWidth: 0,
      }}
    >
      {(isLive || isStarting) ? (
        <span
          aria-hidden="true"
          style={{
            width: 7, height: 7, borderRadius: '50%',
            background: accent.dotBg,
            boxShadow: `0 0 0 4px ${accent.bg}`,
            animation: isLive ? 'allure-event-chip-pulse 1.4s ease-in-out infinite' : undefined,
            flexShrink: 0,
          }}
        />
      ) : (
        <PartyPopper size={compact ? 12 : 13} strokeWidth={2.4} />
      )}
      {/* Title truncates first if room is tight; the time label NEVER
          truncates so guests always see when the event ends. */}
      <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', minWidth: 0, flexShrink: 1 }}>
        {title}
      </span>
      {label ? (
        <span style={{ whiteSpace: 'nowrap', flexShrink: 0, opacity: 0.95 }}>
          · {label}
        </span>
      ) : null}
    </div>
  );
}
